import React, { useState, useEffect } from "react";
import { Row, Col, Card, CardHeader, CardBody, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import classnames from "classnames";
import GeneralStageLayout from "layouts/GeneralStageLayout";
import { useDarkMode } from "context/DarkModeProvider";
import { useLanguage } from "context/LanguageProvider";



const getImageUrl = (imagePath) => {
    if (!imagePath) return null;

    const cleanPath = imagePath.trim();

    console.log("🔍 Original path:", imagePath);
    console.log("🔍 Clean path:", cleanPath);

    if (cleanPath.startsWith("http")) {
        console.log("✅ HTTP URL detected:", cleanPath);
        return cleanPath;
    }

    if (cleanPath.startsWith("/media/")) {
        const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
        const finalUrl = `${baseUrl}${cleanPath}`;
        console.log("✅ Backend image URL:", finalUrl);
        return finalUrl;
    }

    let publicUrl;

    if (cleanPath.startsWith("/")) {
        publicUrl = `${process.env.PUBLIC_URL}${cleanPath}`;
    } else {
        publicUrl = `${process.env.PUBLIC_URL}/${cleanPath}`;
    }

    console.log("✅ Public folder URL:", publicUrl);

    return publicUrl;
}

function parseBoldText(text) {
    const parts = text.split(/(\*\*.*?\*\*)/g);

    return parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            const boldText = part.slice(2, -2);
            return <strong key={index}>{boldText}</strong>;
        }
        return part;
    });
}

function parseCodeBlocks(text) {
    if (!text) return { hasCodeBlocks: false, content: text };

    const codeBlockRegex = /CODEBLOCK\s*\n([\s\S]*?)\n\s*CODEBLOCK/g;

    if (!codeBlockRegex.test(text)) {
        return { hasCodeBlocks: false, content: text };
    }

    codeBlockRegex.lastIndex = 0;

    const result = [];
    let lastIndex = 0;
    let match;
    let blockIndex = 0;

    while ((match = codeBlockRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            const beforeText = text.slice(lastIndex, match.index);
            if (beforeText.trim()) {
                result.push({ type: 'text', content: beforeText });
            }
        }

        result.push({
            type: 'codeblock',
            content: match[1].trim(),
            key: `codeblock-${blockIndex++}`
        });

        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
        const afterText = text.slice(lastIndex);
        if (afterText.trim()) {
            result.push({ type: 'text', content: afterText });
        }
    }

    return { hasCodeBlocks: true, content: result };
}

function detectLanguage(code) {
    if (!code) return '';

    const trimmedCode = code.trim();

    if (trimmedCode.includes('def ') ||
        trimmedCode.includes('import ') ||
        trimmedCode.includes('from ') ||
        trimmedCode.includes('if __name__') ||
        trimmedCode.includes('print(') ||
        trimmedCode.includes('for ') && trimmedCode.includes('in ') ||
        trimmedCode.includes('range(')) {
        return 'Python';
    }

    if (trimmedCode.includes('function ') ||
        trimmedCode.includes('const ') ||
        trimmedCode.includes('let ') ||
        trimmedCode.includes('var ') ||
        trimmedCode.includes('=>') ||
        trimmedCode.includes('console.log')) {
        return 'JavaScript';
    }

    if (trimmedCode.includes('public class') ||
        trimmedCode.includes('private ') ||
        trimmedCode.includes('System.out.println') ||
        trimmedCode.includes('public static void main')) {
        return 'Java';
    }

    if (trimmedCode.includes('#include') ||
        trimmedCode.includes('int main()') ||
        trimmedCode.includes('printf(') ||
        trimmedCode.includes('cout <<')) {
        return 'C++';
    }

    if (trimmedCode.includes('SELECT ') ||
        trimmedCode.includes('FROM ') ||
        trimmedCode.includes('WHERE ') ||
        trimmedCode.includes('INSERT INTO')) {
        return 'SQL';
    }

    if (trimmedCode.includes('<html>') ||
        trimmedCode.includes('<div>') ||
        trimmedCode.includes('<body>')) {
        return 'HTML';
    }

    if (trimmedCode.includes('{') && trimmedCode.includes('}') &&
        (trimmedCode.includes(':') || trimmedCode.includes('color:') || trimmedCode.includes('font-'))) {
        return 'CSS';
    }

    return 'Code';
}

function renderCodeBlockContent(parsedResult, lineProcessor, darkMode) {
    if (!parsedResult.hasCodeBlocks) {
        return lineProcessor(parsedResult.content);
    }

    const elements = [];
    const isDarkMode = typeof darkMode === "boolean"
        ? darkMode
        : localStorage.getItem("darkMode") === "true";

    parsedResult.content.forEach((item, index) => {
        if (item.type === 'codeblock') {
            const detectedLanguage = detectLanguage(item.content);
            elements.push(
                <div key={item.key} style={{ position: 'relative', margin: '16px 0' }}>
                    <div style={{
                        position: 'absolute',
                        top: '-12px',
                        right: '8px',
                        backgroundColor: isDarkMode ? '#334155' : '#e2e8f0',
                        color: isDarkMode ? '#94a3b8' : '#334155',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        border: isDarkMode ? '1px solid #475569' : '1px solid #cbd5e1',
                        zIndex: 1
                    }}>
                        {detectedLanguage}
                    </div>

                    <pre style={{
                        backgroundColor: isDarkMode ? '#1e293b' : '#f8fafc',
                        color: isDarkMode ? '#e2e8f0' : '#0f172a',
                        padding: '16px',
                        borderRadius: '8px',
                        border: isDarkMode ? '1px solid #334155' : '1px solid #dbeafe',
                        overflow: 'auto',
                        fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        whiteSpace: 'pre-wrap',
                        margin: 0
                    }}>
                        <code>{item.content}</code>
                    </pre>
                </div>
            );
        } else if (item.type === 'text') {
            const textElements = lineProcessor(item.content);
            elements.push(...textElements);
        }
    });

    return elements;
}

function parseTextWithImages(text, darkMode) {
    console.log("parseTextWithImages called with:", text);

    if (!text) return [];

    const codeBlockResult = parseCodeBlocks(text);

    if (codeBlockResult.hasCodeBlocks) {
        return renderCodeBlockContent(codeBlockResult, (textContent) => {
            return parseTextLines(textContent, darkMode);
        }, darkMode);
    }

    return parseTextLines(text, darkMode);
}

function parseTextLines(text, darkMode) {
    const lines = text.split("\n");
    const elements = [];
    const textColor = darkMode ? "#ffffff" : "#101010ff";

    lines.forEach((line, index) => {
        const trimmed = line.trim();

        if (trimmed.startsWith("{image:")) {
            const src = trimmed.replace("{image:", "").replace("}", "").trim();
            console.log("Image src extracted:", src);

            const imageUrl = getImageUrl(src);
            console.log("Final image URL:", imageUrl);

            elements.push(
                <div key={`img-${index}`} style={{ textAlign: "center", margin: "20px 0", padding: "10px" }}>
                    <div className="debug-info" style={{ fontSize: "12px", color: "#94a3b8", marginBottom: "10px" }}>
                        🔍 Trying to load: {imageUrl}
                    </div>

                    <img
                        src={imageUrl}
                        alt="Example Image"
                        style={{
                            maxWidth: "100%",
                            height: "auto",
                            borderRadius: "12px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                            border: "2px solid #334155",
                        }}
                        onError={(e) => {
                            console.error('❌ Failed to load image:', imageUrl);
                            console.error('❌ Full URL attempted:', e.target.src);
                            e.target.style.display = 'none';
                            const errorMsg = e.target.parentNode.querySelector('.error-message');
                            if (errorMsg) errorMsg.style.display = 'block';
                        }}
                        onLoad={(e) => {
                            console.log('✅ Image loaded successfully:', imageUrl);
                            const debugInfo = e.target.parentNode.querySelector('.debug-info');
                            if (debugInfo) debugInfo.style.display = 'none';
                        }}
                    />
                    <div className="error-message" style={{ display: 'none', color: '#ef4444', fontSize: '0.9rem', marginTop: '12px', padding: '8px', backgroundColor: '#fee2e2', borderRadius: '6px', border: '1px solid #fecaca' }}>
                        ⚠️ Η εικόνα δεν μπόρεσε να φορτωθεί: <code>{src}</code>
                        <br /><small>Βεβαιωθείτε ότι το αρχείο υπάρχει στον σωστό φάκελο</small>
                        <br /><small>Tried URL: {imageUrl}</small>
                    </div>
                </div>
            );
            return;
        }

        if (trimmed === "" || trimmed.length === 0) {
            elements.push(
                <div key={`empty-${index}`} style={{ height: "1em", color: textColor }} />
            );
            return;
        }

        if (trimmed === "/n" || trimmed === "---") {
            elements.push(
                <div key={`divider-${index}`} style={{ height: "2em", borderBottom: "1px solid #475569", margin: "1em 0" }} />
            );
            return;
        }

        if (trimmed.includes("====")) {
            elements.push(
                <div key={`indent4-${index}`} style={{ marginLeft: "4em", color: textColor }}>
                    {parseBoldText(trimmed.replace(/====/g, ""))}
                </div>
            );
            return;
        }

        if (trimmed.startsWith(">> >>")) {
            elements.push(
                <div key={`indent-double-${index}`} style={{ marginLeft: "4em", color: textColor, fontStyle: "normal" }}>
                    {parseBoldText(trimmed.replace(/^>> >>\s?/, ""))}
                </div>
            );
            return;
        }

        if (trimmed.startsWith("###")) {
            elements.push(
                <div key={`header-${index}`} style={{ fontWeight: "800", color: "#7A8EFF", fontSize: "1.1em", margin: "1em 0 0.5em 0" }}>
                    {parseBoldText(trimmed.replace(/^###\s?/, ""))}
                </div>
            );
            return;
        }

        if (trimmed.startsWith(">>")) {
            elements.push(
                <div key={`indent-${index}`} style={{ marginLeft: "2em", color: textColor }}>
                    {parseBoldText(trimmed.replace(/^>>\s?/, ""))}
                </div>
            );
            return;
        }

        if (trimmed) {
            elements.push(
                <div key={`text-${index}`} style={{ margin: "0.5em 0", lineHeight: "1.6", color: textColor }}>
                    {parseBoldText(trimmed)}
                </div>
            );
        }
    });

    return elements;
}

function parseDescriptionIntoTabs(description, darkMode) {
    if (!description) return [];

    const parts = description.split("***");
    const tabs = [];


    for (let i = 1; i < parts.length; i++) {
        const section = parts[i].trim();
        const lines = section.split("\n");
        const title = lines[0]?.trim() || "Untitled";
        const sectionContent = lines.slice(1).join('\n');
        const codeBlockResult = parseCodeBlocks(sectionContent);

        if (codeBlockResult.hasCodeBlocks) {
            const renderedContent = renderCodeBlockContent(codeBlockResult, (textContent) => {
                return processDescriptionLines(textContent, darkMode);
            });
            tabs.push({ title, content: renderedContent });
        } else {
            tabs.push({ title, content: processDescriptionLines(sectionContent, darkMode) });
        }
    }

    function processDescriptionLines(text, darkMode) {
        const lines = text.split("\n");
        const processedLines = [];
        const textColor = darkMode ? "#ffffff" : "#101010ff";

        for (let j = 0; j < lines.length; j++) {
            const trimmed = lines[j].trim();

            if (trimmed === "" || trimmed.length === 0) {
                processedLines.push(<div key={`empty-${j}`} style={{ height: "1em" }} />);
                continue;
            }

            if (trimmed === "/n" || trimmed === "---") {
                processedLines.push(<div key={`divider-${j}`} style={{ height: "2em", borderBottom: "1px solid #475569", margin: "1em 0" }} />);
                continue;
            }

            if (trimmed.startsWith("{image:")) {
                const src = trimmed.replace("{image:", "").replace("}", "").trim();
                const imageUrl = getImageUrl(src);

                processedLines.push(
                    <div key={`image-${j}`} style={{ textAlign: "center", margin: "20px 0", padding: "10px", borderRadius: "8px" }}>
                        <img
                            src={imageUrl}
                            alt="Description Image"
                            style={{ maxWidth: "100%", height: "auto", borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.2)", border: "2px solid #334155" }}
                            onError={(e) => {
                                console.error('Failed to load image:', imageUrl);
                                e.target.style.display = 'none';
                                e.target.nextElementSibling.style.display = 'block';
                            }}
                            onLoad={() => console.log('Image loaded successfully:', imageUrl)}
                        />
                        <div style={{ display: 'none', color: '#ef4444', fontSize: '0.9rem', marginTop: '12px', padding: '8px', backgroundColor: '#fee2e2', borderRadius: '6px', border: '1px solid #fecaca' }}>
                            ⚠️ Η εικόνα δεν μπόρεσε να φορτωθεί: <code>{src}</code>
                            <br /><small>Βεβαιωθείτε ότι το αρχείο υπάρχει στον σωστό φάκελο</small>
                        </div>
                    </div>
                );
                continue;
            }

            if (trimmed.startsWith(">> >>")) {
                processedLines.push(
                    <div key={`indent-double-${j}`} style={{ marginLeft: "4em", color: textColor, fontStyle: "normal" }}>
                        {parseBoldText(trimmed.replace(/^>> >>\s?/, ""))}
                    </div>
                );
                continue;
            }

            if (trimmed.startsWith(">>")) {
                processedLines.push(
                    <div key={`indent-${j}`} style={{ marginLeft: "2em", color: textColor }}>
                        {parseBoldText(trimmed.replace(/^>>\s?/, ""))}
                    </div>
                );
                continue;
            }

            if (trimmed.startsWith("###")) {
                processedLines.push(
                    <div key={`header-${j}`} style={{ fontWeight: "800", color: "#7A8EFF", fontSize: "1.1em", margin: "1em 0 0.5em 0" }}>
                        {parseBoldText(trimmed.replace(/^###\s?/, ""))}
                    </div>
                );
                continue;
            }

            if (trimmed) {
                processedLines.push(
                    <div key={`text-${j}`} style={{ margin: "0.5em 0", lineHeight: "1.6", color: textColor }}>
                        {parseBoldText(trimmed)}
                    </div>
                );
            }
        }

        return processedLines;
    }

    return tabs;
}

const DescriptionTabs = ({ description, darkMode }) => {
    const tabs = parseDescriptionIntoTabs(description, darkMode);
    const [activeTab, setActiveTab] = useState(0);
    const textColor = darkMode ? "#ffffff" : "#101010ff";

    if (tabs.length === 0) return <p style={{ color: textColor }}>Καμία περιγραφή διαθέσιμη.</p>;

    return (
        <>
            <Nav tabs className="mb-3">
                {tabs.map((tab, index) => (
                    <NavItem key={index}>
                        <NavLink
                            className={classnames("tab-link", { active: activeTab === index })}
                            onClick={() => setActiveTab(index)}
                            style={{
                                marginTop: "3px",
                                color: "#7a8effff",
                                borderBottom: activeTab === index ? "1px solid #5e72e4" : "none",
                                borderLeft: activeTab === index ? "1px solid #5e72e4" : "none",
                                borderRight: activeTab === index ? "1px solid #5e72e4" : "none",
                                borderTop: activeTab === index ? "1px solid #5e72e4" : "none",
                                fontWeight: activeTab === index ? "normal" : "normal",
                                backgroundColor: activeTab === index ? "transparent" : "transparent",
                                boxShadow: activeTab === index ? "0 1px 2px #5e72e4" : "none",
                                cursor: "pointer"
                            }}
                        >
                            {tab.title}
                        </NavLink>
                    </NavItem>
                ))}
            </Nav>
            <TabContent activeTab={activeTab}>
                <TabPane tabId={activeTab}>
                    <div className="markdown-content">
                        {tabs[activeTab]?.content.map((block, idx) => (
                            <div key={idx}>{block}</div>
                        ))}
                    </div>
                </TabPane>
            </TabContent>
        </>
    );
};

const colorSchemes = {
    HashTable: { primary: '#10b981', secondary: '#059669', accent: '#d1fae5' },
    Tree: { primary: '#3b82f6', secondary: '#2563eb', accent: '#dbeafe' },
    List: { primary: '#8b5cf6', secondary: '#7c3aed', accent: '#ede9fe' },
    Stack: { primary: '#f59e0b', secondary: '#d97706', accent: '#fef3c7' },
    Queue: { primary: '#ef4444', secondary: '#dc2626', accent: '#fecaca' },
    Graph: { primary: '#06b6d4', secondary: '#0891b2', accent: '#cffafe' },
    Array: { primary: '#ec4899', secondary: '#db2777', accent: '#fce7f3' },
    Set: { primary: '#84cc16', secondary: '#65a30d', accent: '#ecfccb' },
    Python: { primary: '#3776ab', secondary: '#ffd43b' }

};

const GeneralStage = ({ stageData }) => {
    const screenWidth = window.innerWidth;
    const [, setIsLoading] = useState(true);
    const { darkMode } = useDarkMode();

    const [currentScheme, setCurrentScheme] = useState(() => {
        const s = localStorage.getItem("structureLocation");
        return colorSchemes[s] || colorSchemes.Graph;
    });

    const { selLanguage, toggleLanguage, isLoading } = useLanguage();

    let subCat = null;
    try {
        const raw = localStorage.getItem("SubCategoryClicked");
        subCat = raw ? JSON.parse(raw) : null;
    } catch (e) {
        console.error("Failed to parse SubCategoryClicked:", e);
    }

    return (
        <Col style={{
            height: '100vh',
            overflow: 'auto',
            position: 'relative',
            background: darkMode ? "#0f172a" : "#f8fafc",

        }}>
            <GeneralStageLayout>
                <Row className="mb-4 justify-content-center">
                    <Col
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "flex-start",
                            minHeight: "100vh",
                            padding: "20px 0",
                        }}
                    >
                        <Card
                            style={{
                                width: Math.min(screenWidth * 0.8, 1200),
                                minWidth: 300,
                                maxHeight: "none",
                                backgroundColor: darkMode ? "rgb(30, 41, 59)" : "#ffffff",
                                border: darkMode ? `2px solid ${currentScheme.secondary}30` : `2px solid ${currentScheme.secondary}90`,
                                borderRadius: "24px"
                            }}
                        >
                            <CardHeader style={{
                                backgroundColor: darkMode ? "rgb(30, 41, 59)" : "#ffffff",
                                borderRadius: "24px"
                            }}>
                                <h3
                                    style={{
                                        fontSize: "150%",
                                        fontFamily: "Poppins, sans-serif",
                                        color: "#7a8effff",
                                        backgroundColor: darkMode ? "rgb(30, 41, 59)" : "#ffffff"
                                    }}
                                >
                                    {selLanguage ? "Περιγραφή" : "Description"}
                                </h3>
                            </CardHeader>
                            <CardBody style={{
                                maxHeight: "none",
                                overflow: "visible",
                                wordWrap: "break-word",
                                lineHeight: "1.6",

                            }}>
                                {subCat?.description && subCat.description.includes("***") ? (
                                    <DescriptionTabs
                                        description={selLanguage ? subCat.description : subCat.eng_descr}
                                        darkMode={darkMode}
                                    />
                                ) : (
                                    <div className="markdown-content">
                                        {parseTextWithImages(
                                            selLanguage ? subCat?.description : subCat?.eng_descr,
                                            darkMode
                                        )}
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </GeneralStageLayout>
        </Col>
    );
};

export default GeneralStage;